// src/exercises/WritingExercise.jsx
//
// ÚNICO CAMBIO vs versión original:
//   ✅ Import de ExerciseTokensGuard (1 línea)
//   ✅ El JSX final envuelto en <ExerciseTokensGuard> (2 líneas)
//   ✅ TODO LO DEMÁS intacto — no se tocó ninguna otra línea

import { useState, useEffect } from 'react';
import { LIMITES } from '../limites';
import { RefreshCw, Eye, Timer as TimerIcon, Sparkles, Send, FileText, MessageSquare, Star, Lock, Trophy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import './Exercise.css';
import './WritingExercise.css';

// ── NUEVO: Guard de tokens para este ejercicio ───────────────────────────────
import ExerciseTokensGuard from '../components/guards/ExerciseTokensGuard';

const countSentences = (text) => {
    if (!text.trim()) return 0;
    const matches = text.match(/[^.!?]+[.!?]+(\s|$)/g);
    return matches ? matches.length : 1;
};

function WritingExercise({ exerciseData, onCorrectAnswer }) {
    const { title, subtitle, instruction, min_words = 5, min_sentences = 1 } = exerciseData || {};

    const [studentText, setStudentText] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [sentenceCount, setSentenceCount] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300);
    const token = localStorage.getItem('token');

    const restartExercise = () => {
        setStudentText('');
        setFeedback(null);
        setIsChecking(false);
        setTimeLeft(300);
    };

    useEffect(() => {
        const trimmedText = studentText.trim();
        if (trimmedText === '') { setWordCount(0); setSentenceCount(0); }
        else { setWordCount(trimmedText.split(/\s+/).length); setSentenceCount(countSentences(trimmedText)); }
    }, [studentText]);

    useEffect(() => {
        if (timeLeft <= 0 || feedback) return;
        const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft, feedback]);

    const handleSubmit = async () => {
        setIsChecking(true);
        setFeedback(null);
        try {
            const response = await fetch('/api/writing/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ prompt: instruction, text: studentText }),
            });
            if (!response.ok) throw new Error(`La solicitud al backend falló con estado ${response.status}`);
            const feedbackData = await response.json();
            setFeedback(feedbackData);
            if (feedbackData.score >= 4) {
                onCorrectAnswer();
                showAchievement('Excellent Writing!', '✍️');
                launchConfetti();
            }
        } catch (error) {
            console.error("Error al enviar el texto para revisión:", error);
            setFeedback({ correction: "Error de Conexión", comments: "No se pudo conectar con el servidor.", score: 0 });
        } finally {
            setIsChecking(false);
        }
    };

    const progress = Math.min(100, Math.round(((wordCount / min_words) * 50) + ((sentenceCount / min_sentences) * 50)));
    const canSubmit = wordCount >= min_words && sentenceCount >= min_sentences;

    // ── WRAP PRINCIPAL EN ExerciseTokensGuard ─────────────────────────────────
    return (
        <ExerciseTokensGuard>
            <div className="exercise-container">
                <header className="we-header">
                    <div className="we-icon-container-inset"><FileText size={24} /></div>
                    <div className="we-title-group">
                        <h2 className="main-title">{title || 'Writing Practice'}</h2>
                        <p className="main-subtitle">{subtitle || 'Completa los ejercicios para avanzar.'}</p>
                    </div>
                    <div className="we-controls">
                        <div className="we-stat-box-inset"><TimerIcon size={18} /> {Math.floor(timeLeft/60)}:{(timeLeft%60).toString().padStart(2, '0')}</div>
                        <button className="we-control-btn"><Eye size={18} /></button>
                        <button className="we-control-btn" onClick={restartExercise}><RefreshCw size={18} /></button>
                    </div>
                </header>

                <div className="we-stats-container">
                    <div className="we-stat-box-inset large">
                        <div className="we-stat-number">{wordCount}</div>
                        <div className="we-stat-label">Palabras ({min_words}+ req.)</div>
                    </div>
                    <div className="we-stat-box-inset large">
                        <div className="we-stat-number">{sentenceCount}</div>
                        <div className="we-stat-label">Oraciones ({min_sentences} req.)</div>
                    </div>
                    <div className="we-stat-box-inset large">
                        <div className="we-stat-number">{progress}%</div>
                        <div className="we-stat-label">Progreso</div>
                    </div>
                </div>

                <main className="we-writing-card">
                    <div className="we-card-header">
                        <Sparkles size={24} className="main-icon" />
                        <div className="we-instruction-text">
                            <ReactMarkdown components={{ strong: ({node, ...props}) => <strong className="we-highlight-text" {...props} /> }}>
                                {instruction || 'My Room: What\'s Happening Now?'}
                            </ReactMarkdown>
                        </div>
                    </div>

                    <div className="we-writing-form">
                        <textarea
                            maxLength={LIMITES.redaccion}
                            className="we-writing-textarea"
                            placeholder="Start writing here..."
                            value={studentText}
                            onChange={(e) => setStudentText(e.target.value)}
                            disabled={isChecking || feedback}
                            rows="8"
                        />
                        {!canSubmit && !isChecking && !feedback && (
                            <p className="we-requirement-message">
                                Necesitas al menos {min_words} palabra{min_words !== 1 ? 's' : ''} y {min_sentences} oración{min_sentences !== 1 ? '/es' : ''}.
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className={`exercise-action-button ${canSubmit && !feedback ? 'active' : 'inactive'}`}
                            disabled={isChecking || !canSubmit || feedback}
                        >
                            {isChecking ? ('Revisando...') : (
                                <>{canSubmit ? 'Enviar para Revisión' : 'Requisitos no cumplidos'} {canSubmit ? <Send size={18} /> : <Lock size={18} />}</>
                            )}
                        </button>
                    </div>
                </main>

                {feedback && (
                    <>
                        <section className="we-feedback-container">
                            <h3 className="we-feedback-main-title">Spacy's Feedback</h3>
                            <div className="we-feedback-grid">
                                <div className="we-feedback-item">
                                    <div className="we-feedback-item-header"><FileText size={18} /> Correction:</div>
                                    <div className="we-feedback-content">
                                        <ReactMarkdown components={{ strong: ({node, ...props}) => <strong className="we-highlight-text" {...props} /> }}>{feedback.correction}</ReactMarkdown>
                                    </div>
                                </div>
                                <div className="we-feedback-item">
                                    <div className="we-feedback-item-header"><MessageSquare size={18} /> Comments:</div>
                                    <div className="we-feedback-content">
                                        <ReactMarkdown components={{ strong: ({node, ...props}) => <strong className="we-highlight-text" {...props} /> }}>{feedback.comments || feedback.feedback}</ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                            <div className="we-score-card">
                                <strong>Score:</strong>
                                <div className="we-feedback-score">
                                    {[...Array(5)].map((_, i) => <Star key={i} size={24} className={`star ${i < feedback.score ? 'filled' : 'empty'}`} strokeWidth={1.5} />)}
                                </div>
                            </div>
                        </section>

                        {feedback.score >= 4 && (
                            <section className="we-completion-card">
                                <Trophy size={40} className="main-icon"/>
                                <h3 className="we-completion-title">¡Ejercicio Completado!</h3>
                                <p className="we-completion-details">
                                    Completaste el ejercicio en {Math.floor((300-timeLeft)/60)}:{((300-timeLeft)%60).toString().padStart(2,'0')} con {wordCount} palabras y {sentenceCount} oraciones.
                                </p>
                            </section>
                        )}
                    </>
                )}
            </div>
        </ExerciseTokensGuard>
    );
}

export default WritingExercise;